// Inline tone-check strip for the reply composer.
//
// Watches the body for ~2s of typing inactivity, then asks the
// `check-reply-tone` Edge Function to classify. Renders nothing while the
// body is too short or the check returns "good" — visible only when the
// tone-check actually flags something. Click ✕ to dismiss the warning for
// this body content (re-checks if the body changes again).
//
// Never blocks Send. The user can ship whatever they want — this is purely
// advisory, like Gmail's "you may have forgotten an attachment" hint.

import { useEffect, useState } from 'react';
import { AlertTriangle, Sparkles, X } from 'lucide-react';
import { useReplyToneCheck } from '@/lib/queries';
import { cn } from '@/lib/utils';

interface Props {
  /** Plain-text body of the reply. */
  bodyText: string;
  /** Master enable flag — `false` short-circuits any network call. */
  enabled?: boolean;
}

const DEBOUNCE_MS = 2000;

export function ToneCheckStrip({ bodyText, enabled = true }: Props) {
  // Debounce the body — only commit a snapshot to the query key after
  // typing pauses for DEBOUNCE_MS. Otherwise every keystroke would invalidate
  // the cache and we'd hit Anthropic constantly.
  const [debouncedBody, setDebouncedBody] = useState(bodyText);
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedBody(bodyText), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [bodyText]);

  // Per-body dismiss — once the user closes the warning for a given body
  // content, don't show it again until they edit. Tracking by trimmed body
  // means trivial whitespace edits don't re-show a dismissed warning.
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);

  const q = useReplyToneCheck(debouncedBody, enabled);

  if (!enabled) return null;
  // Render nothing until we have a result. The hook returns isLoading
  // briefly during the API call — we don't show a "checking…" indicator
  // because it's noise; the warning either appears or doesn't.
  if (!q.data) return null;
  if (q.data.tone === 'good') return null;
  // The service is advisory: a reply without a concerns list (an error body, an old
  // version) must show nothing, not take the whole ticket page down.
  if (!Array.isArray(q.data.concerns) || q.data.concerns.length === 0) return null;

  // Hide if the user dismissed for this exact body content.
  if (dismissedFor === debouncedBody.trim()) return null;

  // Highest severity drives the colour. Yellow for low/medium, rose for
  // high. Most flags are low/medium — we don't want everyone tuning out
  // the panel because it's always rose.
  const maxSeverity = q.data.concerns.reduce<'low' | 'medium' | 'high'>(
    (acc, c) => (severityRank(c.severity) > severityRank(acc) ? c.severity : acc),
    'low',
  );
  const tone = maxSeverity === 'high' ? 'rose' : 'amber';

  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-2 border-b px-3 py-2 text-2xs',
        tone === 'rose'
          ? 'border-tint-critical-ring bg-tint-critical text-tint-critical-fg'
          : 'border-tint-warning-ring bg-tint-warning text-tint-warning-fg',
      )}
    >
      {tone === 'rose' ? (
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      ) : (
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-semibold">
          Tone check
          <span className="ml-1 font-normal opacity-70">· advisory only</span>
        </p>
        <ul className="mt-0.5 space-y-0.5">
          {q.data.concerns.map((c, i) => (
            <li key={i} className="leading-snug">
              {c.message}
            </li>
          ))}
        </ul>
      </div>
      <button
        type="button"
        onClick={() => setDismissedFor(debouncedBody.trim())}
        className="shrink-0 rounded p-0.5 transition-colors hover:bg-black/10"
        aria-label="Dismiss tone-check warning"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

function severityRank(s: 'low' | 'medium' | 'high'): number {
  return s === 'high' ? 3 : s === 'medium' ? 2 : 1;
}
